import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getInventoryItemByInventoryItemIdIssueToLabour,
  createIssueToLabour,
  updateIssueToLabour,
  fetchIssueToLabour,
  fetchIssueToLabourById,
} from "./thunk";

// -------------------------------------------
// TYPES
// -------------------------------------------
interface IssueToLabourItem {
  [key: string]: any;
}

interface IssueToLabourState {
  list: IssueToLabourItem[];
  rowCount: number;
  loading: boolean;
  submitting: boolean;
  updating: boolean;
  selectedIssue: IssueToLabourItem | null;
  error: string | null;
  validationErrors?: Record<string, string[]>;
  inventoryItem: IssueToLabourItem | null;
  inventoryLoading: boolean;
}

// -------------------------------------------
// INITIAL STATE
// -------------------------------------------
const initialState: IssueToLabourState = {
  list: [],
  rowCount: 0,
  loading: false,
  submitting: false,
  updating: false,
  selectedIssue: null,
  error: null,
  inventoryItem: null,
  inventoryLoading: false,
};

// -------------------------------------------
// SLICE
// -------------------------------------------
export const issueToLabourSlice = createSlice({
  name: "IssueToLabour",
  initialState,
  reducers: {
    clearIssueToLabourDetails(state) {
      state.selectedIssue = null;
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    // -----------------------------------
    // FETCH LIST
    // -----------------------------------
    builder.addCase(fetchIssueToLabour.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchIssueToLabour.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload.data;
      state.rowCount = action.payload.rowCount;
    });
    builder.addCase(fetchIssueToLabour.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // -----------------------------------
    // GET BY ID
    // -----------------------------------
    builder.addCase(fetchIssueToLabourById.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.selectedIssue = null;
    });
    builder.addCase(fetchIssueToLabourById.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedIssue = action.payload;
    });
    builder.addCase(fetchIssueToLabourById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // -----------------------------------
    // GET INVENTORY ITEM
    // -----------------------------------
    builder.addCase(
      getInventoryItemByInventoryItemIdIssueToLabour.pending,
      (state) => {
        state.inventoryLoading = true;
        state.error = null;
      }
    );
    builder.addCase(
      getInventoryItemByInventoryItemIdIssueToLabour.fulfilled,
      (state, action) => {
        state.inventoryLoading = false;
        state.inventoryItem = action.payload;
      }
    );
    builder.addCase(
      getInventoryItemByInventoryItemIdIssueToLabour.rejected,
      (state, action) => {
        state.inventoryLoading = false;
        state.error = action.payload as string;
      }
    );

    // -----------------------------------
    // CREATE
    // -----------------------------------
    builder.addCase(createIssueToLabour.pending, (state) => {
      state.submitting = true;
      state.error = null;
      state.validationErrors = undefined;
    });
    builder.addCase(createIssueToLabour.fulfilled, (state) => {
      state.submitting = false;
    });
    builder.addCase(createIssueToLabour.rejected, (state, action: any) => {
      state.submitting = false;

      if (typeof action.payload === "object") {
        state.validationErrors = action.payload.errors;
        state.error = action.payload.message;
      } else {
        state.error = action.payload;
      }
    });

    // -----------------------------------
    // UPDATE
    // -----------------------------------
    builder.addCase(updateIssueToLabour.pending, (state) => {
      state.updating = true;
      state.error = null;
      state.validationErrors = undefined;
    });
    builder.addCase(updateIssueToLabour.fulfilled, (state) => {
      state.updating = false;
    });
    builder.addCase(updateIssueToLabour.rejected, (state, action: any) => {
      state.updating = false;

      if (typeof action.payload === "object") {
        state.validationErrors = action.payload.errors;
        state.error = action.payload.message;
      } else {
        state.error = action.payload;
      }
    });
  },
});

// -------------------------------------------
// EXPORT REDUCER & ACTIONS
// -------------------------------------------
export const { clearIssueToLabourDetails } = issueToLabourSlice.actions;

export default issueToLabourSlice.reducer;
